import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  BlacklistOption,
  RestrictedAdvancedSearchValues,
  RestrictedSearchValues,
} from 'components/Compliance/Restricted/BlackListSearchModal/models/restricted-search-values';
import { BlackListValues } from 'components/Compliance/Restricted/BlacklistHistory/RestrictedDetail/RestrictedDetailModal/RestrictedDetailForm/models/black-list-values';

import type {
  BlacklistedPerson,
  BlackListPersonMatches,
} from 'commons/modules/service/black-list';

import {
  getBlackListPerson,
  saveBlackListMatches,
  uploadDocumnetsBlackList,
} from 'service/black-list';

import { arrayValidation } from 'utils/array';

import { useSnackbar } from 'hooks';

import { setRefreshData, getRefreshData } from 'store/blackList';

import { ERROR_TRYING_TO_SAVE, SAVED_SUCCESSFULLY } from 'constants/messages';

const useBlackList = (coincidences: BlacklistOption[] = []) => {
  const dispatch = useDispatch();
  const refreshData = useSelector(getRefreshData);
  const { showSnackbarSuccess, showSnackbarError, showSnackbarInfo } =
    useSnackbar();

  const [showLoader, setShowLoader] = useState(false);
  const [blacklistOptions, setBlacklistOptions] = useState<BlacklistOption[]>(
    coincidences || [],
  );
  const [showModalRestricted, sewtShowModalRestricted] =
    useState<boolean>(false);

  const getBlackList = async (
    values: RestrictedSearchValues,
  ): Promise<BlacklistOption> => {
    const { success, data: dataResp } = await getBlackListPerson(values);
    if (success && dataResp && dataResp.persona) {
      return {
        filter: values,
        rows: dataResp.persona,
      };
    }
    return {
      filter: values,
      rows: [],
    };
  };

  const hasBlackListMatches = (respBlacklistOptions: BlacklistOption[]) => {
    if (!arrayValidation(respBlacklistOptions)) return false;

    let hasCoincidences = false;

    for (let index = 0; index < respBlacklistOptions.length; index++) {
      const element = respBlacklistOptions[index];

      if (arrayValidation(element.rows)) {
        hasCoincidences = true;
        break;
      }
    }

    return hasCoincidences;
  };

  const getBlacklistMatches = async (
    values: RestrictedAdvancedSearchValues,
  ) => {
    setShowLoader(true);
    setBlacklistOptions([]);
    const response = values.filters.map((value) => getBlackList(value));
    const personsTableTemp = await Promise.all(response);
    setBlacklistOptions(personsTableTemp);
    setShowLoader(false);
    return personsTableTemp;
  };

  const processSaveBlacklistMatches = async (
    matchValues: BlacklistedPerson[],
    row: BlackListPersonMatches,
    formValues: BlackListValues,
  ) => {
    if (!arrayValidation(matchValues)) {
      showSnackbarInfo('No hay informaci\u00F3n por guardar');
      return { success: false };
    }
    setShowLoader(true);
    const body = {
      processId: row.processId,
      sequentialId: row.sequentialId,
      persons: matchValues.map((value) => ({
        id: value.id,
        match: value.match || false,
      })),
    };

    const resp = await saveBlackListMatches(body);
    await uploadDocumnetsBlackList({
      processId: `${row.processId}`,
      sequentialId: row.sequentialId,
      comments: formValues.comment,
      file: formValues.file,
    });
    setShowLoader(false);
    const { success, errorMessage } = resp;

    if (success) {
      showSnackbarSuccess(SAVED_SUCCESSFULLY);
      dispatch(setRefreshData(refreshData + 1));
      return resp;
    }
    showSnackbarError(errorMessage || ERROR_TRYING_TO_SAVE);
    return resp;
  };

  const handleCancelRestricted = () => {
    sewtShowModalRestricted(false);
  };

  return {
    showLoader,
    blacklistOptions,
    showModalRestricted,
    setShowLoader,
    getBlacklistMatches,
    hasBlackListMatches,
    processSaveBlacklistMatches,
    sewtShowModalRestricted,
    handleCancelRestricted,
  };
};

export default useBlackList;
